// Depreciation calculation utilities

/**
 * Calculate straight line depreciation
 * @param {number} cost - Original cost of the asset
 * @param {number} salvageValue - Estimated salvage value
 * @param {number} usefulLife - Useful life in years
 * @param {number} currentYear - Current year (0-based)
 * @returns {Object} Depreciation calculation result
 */
export const calculateStraightLineDepreciation = (cost, salvageValue, usefulLife, currentYear = 0) => {
  const annualDepreciation = (cost - salvageValue) / usefulLife;
  const accumulatedDepreciation = annualDepreciation * currentYear;
  const bookValue = cost - accumulatedDepreciation;
  
  return {
    annualDepreciation,
    accumulatedDepreciation,
    bookValue,
    remainingValue: bookValue - salvageValue
  };
};

/**
 * Calculate declining balance depreciation
 * @param {number} cost - Original cost of the asset
 * @param {number} rate - Depreciation rate (as decimal, e.g., 0.2 for 20%)
 * @param {number} currentYear - Current year (0-based)
 * @param {number} salvageValue - Estimated salvage value
 * @returns {Object} Depreciation calculation result
 */
export const calculateDecliningBalanceDepreciation = (cost, rate, currentYear = 0, salvageValue = 0) => {
  let bookValue = cost;
  let accumulatedDepreciation = 0;
  let annualDepreciation = 0;
  
  for (let year = 0; year <= currentYear; year++) {
    if (year === currentYear) {
      annualDepreciation = Math.max(0, bookValue * rate);
      // Ensure book value doesn't go below salvage value
      if (bookValue - annualDepreciation < salvageValue) {
        annualDepreciation = Math.max(0, bookValue - salvageValue);
      }
    } else {
      const yearDepreciation = Math.max(0, bookValue * rate);
      if (bookValue - yearDepreciation < salvageValue) {
        bookValue = salvageValue;
        accumulatedDepreciation = cost - salvageValue;
        break;
      }
      bookValue -= yearDepreciation;
      accumulatedDepreciation += yearDepreciation;
    }
  }
  
  bookValue = cost - accumulatedDepreciation - annualDepreciation;
  
  return {
    annualDepreciation,
    accumulatedDepreciation,
    bookValue,
    remainingValue: Math.max(0, bookValue - salvageValue)
  };
};

/**
 * Calculate sum of years digits depreciation
 * @param {number} cost - Original cost of the asset
 * @param {number} salvageValue - Estimated salvage value
 * @param {number} usefulLife - Useful life in years
 * @param {number} currentYear - Current year (0-based)
 * @returns {Object} Depreciation calculation result
 */
export const calculateSumOfYearsDepreciation = (cost, salvageValue, usefulLife, currentYear = 0) => {
  const sumOfYears = (usefulLife * (usefulLife + 1)) / 2;
  const depreciableAmount = cost - salvageValue;
  
  let accumulatedDepreciation = 0;
  let annualDepreciation = 0;
  
  for (let year = 0; year <= currentYear; year++) {
    const remainingLife = usefulLife - year;
    const yearDepreciation = (remainingLife / sumOfYears) * depreciableAmount;
    
    if (year === currentYear) {
      annualDepreciation = yearDepreciation;
    }
    accumulatedDepreciation += yearDepreciation;
  }
  
  const bookValue = cost - accumulatedDepreciation;
  
  return {
    annualDepreciation,
    accumulatedDepreciation,
    bookValue,
    remainingValue: Math.max(0, bookValue - salvageValue)
  };
};

/**
 * Calculate units of production depreciation
 * @param {number} cost - Original cost of the asset
 * @param {number} salvageValue - Estimated salvage value
 * @param {number} totalUnits - Total estimated units of production
 * @param {number} currentUnits - Current units produced
 * @returns {Object} Depreciation calculation result
 */
export const calculateUnitsOfProductionDepreciation = (cost, salvageValue, totalUnits, currentUnits = 0) => {
  const depreciableAmount = cost - salvageValue;
  const depreciationPerUnit = depreciableAmount / totalUnits;
  const accumulatedDepreciation = depreciationPerUnit * currentUnits;
  const bookValue = cost - accumulatedDepreciation;
  
  return {
    depreciationPerUnit,
    accumulatedDepreciation,
    bookValue,
    remainingValue: Math.max(0, bookValue - salvageValue)
  };
};

/**
 * Calculate depreciation based on method
 * @param {string} method - Depreciation method
 * @param {Object} params - Calculation parameters
 * @returns {Object} Depreciation calculation result
 */
export const calculateDepreciation = (method, params) => {
  const {
    cost,
    salvageValue = 0,
    usefulLife,
    rate,
    currentYear = 0,
    totalUnits,
    currentUnits = 0
  } = params;

  switch (method.toLowerCase()) {
    case 'straight line':
      return calculateStraightLineDepreciation(cost, salvageValue, usefulLife, currentYear);
    
    case 'declining balance':
      return calculateDecliningBalanceDepreciation(cost, rate, currentYear, salvageValue);
    
    case 'sum of years':
      return calculateSumOfYearsDepreciation(cost, salvageValue, usefulLife, currentYear);
    
    case 'units of production':
      return calculateUnitsOfProductionDepreciation(cost, salvageValue, totalUnits, currentUnits);
    
    default:
      throw new Error(`Unknown depreciation method: ${method}`);
  }
};

/**
 * Generate depreciation schedule for an asset
 * @param {string} method - Depreciation method
 * @param {Object} params - Asset parameters
 * @returns {Array} Depreciation schedule
 */
export const generateDepreciationSchedule = (method, params) => {
  const {
    cost,
    salvageValue = 0,
    usefulLife,
    rate,
    totalUnits
  } = params;

  const schedule = [];

  switch (method.toLowerCase()) {
    case 'straight line':
      for (let year = 0; year < usefulLife; year++) {
        const calculation = calculateStraightLineDepreciation(cost, salvageValue, usefulLife, year);
        schedule.push({
          year: year + 1,
          ...calculation
        });
      }
      break;

    case 'declining balance':
      for (let year = 0; year < usefulLife; year++) {
        const calculation = calculateDecliningBalanceDepreciation(cost, rate, year, salvageValue);
        schedule.push({
          year: year + 1,
          ...calculation
        });
      }
      break;

    case 'sum of years':
      for (let year = 0; year < usefulLife; year++) {
        const calculation = calculateSumOfYearsDepreciation(cost, salvageValue, usefulLife, year);
        schedule.push({
          year: year + 1,
          ...calculation
        });
      }
      break;

    case 'units of production':
      if (totalUnits) {
        const unitsPerYear = Math.ceil(totalUnits / usefulLife);
        for (let year = 0; year < usefulLife; year++) {
          const currentUnits = Math.min((year + 1) * unitsPerYear, totalUnits);
          const calculation = calculateUnitsOfProductionDepreciation(cost, salvageValue, totalUnits, currentUnits);
          schedule.push({
            year: year + 1,
            unitsProduced: currentUnits,
            ...calculation
          });
        }
      }
      break;

    default:
      throw new Error(`Unknown depreciation method: ${method}`);
  }

  return schedule;
};

/**
 * Calculate asset impairment
 * @param {number} carryingAmount - Current carrying amount
 * @param {number} recoverableAmount - Recoverable amount
 * @returns {Object} Impairment calculation result
 */
export const calculateImpairment = (carryingAmount, recoverableAmount) => {
  const impairmentLoss = Math.max(0, carryingAmount - recoverableAmount);
  const newCarryingAmount = carryingAmount - impairmentLoss;
  
  return {
    impairmentLoss,
    newCarryingAmount,
    isImpaired: impairmentLoss > 0
  };
};

/**
 * Format currency value
 * @param {number} value - Value to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(value);
};

/**
 * Calculate percentage change
 * @param {number} oldValue - Old value
 * @param {number} newValue - New value
 * @returns {number} Percentage change
 */
export const calculatePercentageChange = (oldValue, newValue) => {
  if (oldValue === 0) return 0;
  return ((newValue - oldValue) / oldValue) * 100;
};
